/**
 * AuditStageIndicator — stage label + elapsed-time readout for an
 * in-progress audit (pirx-qydoj.8). Rendered by StatusBadge as the content
 * of a hover/focus tooltip on the PROCESSING badge (History table), rather
 * than stacked inline below it — `currentStep` can legitimately be null for
 * stretches of a PROCESSING audit (e.g. right after claim, before the
 * pipeline writes its first stage label), so a permanently-visible second
 * line would show up inconsistently row to row.
 *
 * Renders nothing when `currentStep` is null or unrecognized. When present,
 * shows a translated stage label in an aria-live="polite" region (only
 * announces on label change, not on every tick) plus a client-side ticking
 * elapsed-time readout seeded from `processingStartedAt`.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AuditProcessingStage } from "@vardic/shared/types";
import { resolveStageLabel } from "./audit-stage-label";

interface AuditStageIndicatorProps {
  currentStep: AuditProcessingStage | null | undefined;
  processingStartedAt: string | null | undefined;
}

/** Format elapsed seconds as m:ss or h:mm:ss. */
function formatElapsed(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const ss = s.toString().padStart(2, "0");
  if (h > 0) {
    const mm = m.toString().padStart(2, "0");
    return `${h}:${mm}:${ss}`;
  }
  return `${m}:${ss}`;
}

export function AuditStageIndicator({
  currentStep,
  processingStartedAt,
}: AuditStageIndicatorProps) {
  const { t } = useTranslation("history");
  const stageLabel = resolveStageLabel(currentStep, t);

  // Elapsed-time readout — ticks every second from processingStartedAt.
  // Seeded at 0 and resynced immediately in the effect below (rather than
  // computed here via Date.now()) because render must stay pure — an
  // impure call in the initializer would produce unstable results if React
  // re-renders the component without remounting it.
  const startMs = processingStartedAt
    ? new Date(processingStartedAt).getTime()
    : null;
  const [elapsedMs, setElapsedMs] = useState<number>(0);

  useEffect(() => {
    if (startMs === null) return;
    // Resync immediately when the seed timestamp changes. This is the
    // documented exception for syncing an effect to an external clock
    // (Date.now) rather than a derivable-from-render value — skipping the
    // synchronous call here would flash a stale "0:00" until the first
    // 1s interval tick.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    setElapsedMs(Date.now() - startMs);
    const id = setInterval(() => {
      setElapsedMs(Date.now() - startMs);
    }, 1000);
    return () => clearInterval(id);
  }, [startMs]);

  // Nothing to show if there's no recognized stage label.
  if (!stageLabel) return null;

  return (
    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
      {/* aria-live="polite": announces only when stageLabel changes, not on
          every elapsed-time tick, so screen readers are not spammed. The
          elapsed time node is intentionally outside this region. */}
      <span aria-live="polite" data-test="audit-stage-label">
        {stageLabel}
      </span>
      {startMs !== null && (
        <span
          className="tabular-nums"
          aria-hidden="true"
          data-test="audit-stage-elapsed"
        >
          · {formatElapsed(elapsedMs)}
        </span>
      )}
    </div>
  );
}
